/**
 * On-device OCR with tesseract.js (WASM, in a Web Worker). Every file is served by the app itself
 * from ./ocr/ (copied by scripts/copy-ocr-assets.mjs and precached by the service worker): no
 * CDN, no upload. The tesseract.js library is loaded only when a receipt is read.
 */
export interface OcrLine {
  text: string;
  /** 0–100, as reported by Tesseract. */
  confidence: number;
}

export interface OcrResult {
  text: string;
  lines: OcrLine[];
  engine: string;
}

export type OcrProgress = (fraction: number) => void;

/** Injected into the data layer, so tests can use a fake engine. */
export type OcrEngine = (image: Blob, onProgress?: OcrProgress) => Promise<OcrResult>;

export const OCR_ENGINE_ID = "tesseract.js@7.0.0/ita-best_int";

export class OcrError extends Error {
  override readonly name = "OcrError";
}

/** WebAssembly SIMD support (same probe as wasm-feature-detect): picks the faster engine build. */
function supportsWasmSimd(): boolean {
  try {
    return WebAssembly.validate(new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]));
  } catch {
    return false;
  }
}

const assetUrl = (name: string) => new URL(`ocr/${name}`, document.baseURI).href;

export const tesseractEngine: OcrEngine = async (image, onProgress) => {
  const { createWorker, OEM, PSM } = await import("tesseract.js");
  let worker: Awaited<ReturnType<typeof createWorker>> | undefined;
  try {
    worker = await createWorker("ita", OEM.LSTM_ONLY, {
      workerPath: assetUrl("worker.min.js"),
      corePath: assetUrl(supportsWasmSimd() ? "tesseract-core-simd-lstm.wasm.js" : "tesseract-core-lstm.wasm.js"),
      langPath: new URL("ocr", document.baseURI).href,
      workerBlobURL: false,
      // The service worker already keeps these files offline: no second copy in IndexedDB.
      cacheMethod: "none",
      gzip: true,
      logger: (m: { status: string; progress: number }) => {
        if (m.status === "recognizing text") onProgress?.(m.progress);
      },
      // A failed job still rejects `recognize` (handled below); this only stops tesseract.js from
      // also throwing the same error, uncaught, from its message handler.
      errorHandler: () => undefined,
      // Init parameters, written to a config file by tesseract.js (its types list only a few):
      // Tesseract's diagnostics ("failed to load ita.special-words", resolution estimates) are not
      // errors and must not fill the console.
    }, { debug_file: "/dev/null" } as Partial<import("tesseract.js").InitOptions>);
    // Receipts and screenshots are a single column of lines.
    await worker.setParameters({ tessedit_pageseg_mode: PSM.SINGLE_COLUMN, preserve_interword_spaces: "1" });
    const { data } = await worker.recognize(image, {}, { text: true, blocks: true });
    const lines = (data.blocks ?? []).flatMap((b) => b.paragraphs.flatMap((p) => p.lines.map((l) => ({ text: l.text.replace(/\n$/, ""), confidence: l.confidence }))));
    return { text: data.text, lines, engine: OCR_ENGINE_ID };
  } catch (error) {
    throw new OcrError(`Lettura della ricevuta non riuscita (${(error as Error)?.message ?? String(error)}).`);
  } finally {
    // One worker per read: frees the engine's memory (~100 MB) on phones.
    await worker?.terminate().catch(() => undefined);
  }
};
